import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Carousel,
  CarouselItem,
  Heading,
  Modal,
  ModalBody,
  ModalHeader,
} from '@americanexpress/dls-react';

const formatCurrency = (amount, currency) => {
  if (typeof amount !== 'number' || !currency) {
    return null;
  }

  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

const formatCancellationDate = (cancelTime) => {
  if (!cancelTime) {
    return null;
  }

  const parsedDate = new Date(cancelTime);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toLocaleDateString();
};

const RoomImageColumn = ({ roomId, roomName, photos }) => (
  <div style={{ width: '35%', flexShrink: 0 }}>
    {photos.length > 1 && (
      <Carousel
        id={`room-carousel-${roomId}`}
        isNavigationOnSlides={true}
        labelOverrides={{ 'aria-label': `${roomName} images` }}
      >
        {photos.map((photo, index) => (
          <CarouselItem
            key={photo.url}
            id={`room-${roomId}-photo-${index}`}
            style={{ height: '240px' }}
          >
            <img
              src={photo.url}
              alt={photo.alt || roomName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </CarouselItem>
        ))}
      </Carousel>
    )}
    {photos.length === 1 && (
      <img
        src={photos[0]?.url}
        alt={photos[0]?.alt || roomName}
        className="radius-container-sm"
        style={{
          width: '100%',
          height: '240px',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    )}
    {photos.length === 0 && (
      <div
        aria-label="No room image available"
        className="radius-container-sm flex flex-align-items-center flex-justify-center"
        style={{
          width: '100%',
          height: '240px',
          backgroundColor: '#f2f2f2',
        }}
      >
        <span className="body-2 color-gray-01">No room image available</span>
      </div>
    )}
  </div>
);

const RoomPricingColumn = ({
  formattedPrice,
  formattedStrikethroughPrice,
  boardLabel,
  refundLabel,
  cancellationDate,
}) => (
  <div style={{ width: '20%', minWidth: '140px', flexShrink: 0 }} className="text-align-right">
    {formattedPrice ? (
      <p className="body-bold margin-0">{formattedPrice}</p>
    ) : null}
    {formattedStrikethroughPrice && formattedStrikethroughPrice !== formattedPrice ? (
      <p className="legal-1">
        <s>{formattedStrikethroughPrice}</s>
      </p>
    ) : null}
    {[boardLabel, refundLabel].filter(Boolean).length > 0 ? (
      <p className="body margin-0 margin-1-t">
        {[boardLabel, refundLabel].filter(Boolean).join(' • ')}
      </p>
    ) : null}
    {cancellationDate ? (
      <p className="legal-1 margin-0 margin-1-t">
        Free cancellation until
        {' '}
        {cancellationDate}
      </p>
    ) : null}
    <Button className="margin-t">
      Select Room
    </Button>
  </div>
);

// eslint-disable-next-line complexity -- the card conditionally renders details, pricing, and modal content
const HotelRoomCard = ({ room, isLast = false }) => {
  const {
    id,
    name,
    description,
    amenities = [],
    detailsVariant = 'modal',
    size,
    maxOccupancy,
    bedSummary,
    photos = [],
    pricing,
  } = room;
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const metaItems = [
    size ? `${size.value} ${size.unit}` : null,
    maxOccupancy ? `Sleeps ${maxOccupancy}` : null,
  ].filter(Boolean);
  const hasInlineDetails = ['inline', 'inline-modal'].includes(detailsVariant) && Boolean(description);
  const hasModalDetails = ['modal', 'inline-modal'].includes(detailsVariant)
    && (amenities.length > 0 || Boolean(description));

  const formattedPrice = formatCurrency(pricing?.amount, pricing?.currency);
  const formattedStrikethroughPrice = formatCurrency(
    pricing?.strikethroughAmount,
    pricing?.strikethroughCurrency || pricing?.currency
  );
  const cancellationDate = formatCancellationDate(pricing?.freeCancellationUntil);

  return (
    <div className={`flex${isLast ? '' : ' border-b pad-3-b margin-3-b'}`}>
      <RoomImageColumn roomId={id} roomName={name} photos={photos} />

      <div className="flex flex-column flex-item-grow pad-2-lr">
        <Heading level={3} variant="sans-small-bold">{name}</Heading>
        {metaItems.length > 0 && (
          <p className="body margin-1-t margin-0-b">{metaItems.join(' · ')}</p>
        )}
        {bedSummary && (
          <p className="margin-1-t margin-0-b">{bedSummary}</p>
        )}
        {hasInlineDetails && (
          <p className="body margin-1-t margin-0-b">{description}</p>
        )}
        {hasModalDetails && (
          <Button
            variant="secondary"
            className="margin-t"
            style={{ alignSelf: 'flex-start', width: 'fit-content' }}
            onClick={() => setIsDetailsModalOpen(true)}
          >
            More Details
          </Button>
        )}
      </div>

      {pricing && (
        <RoomPricingColumn
          formattedPrice={formattedPrice}
          formattedStrikethroughPrice={formattedStrikethroughPrice}
          boardLabel={pricing?.boardLabel}
          refundLabel={pricing?.refundLabel}
          cancellationDate={cancellationDate}
        />
      )}

      {hasModalDetails && isDetailsModalOpen && (
        <Modal
          aria-labelledby={`room-details-${id}-heading`}
          aria-describedby={`room-details-${id}-body`}
          innerContainerClassName="width-full"
          onClose={() => setIsDetailsModalOpen(false)}
        >
          <ModalHeader id={`room-details-${id}-heading`}>
            <Heading level={2} variant="sans-medium-bold">
              {name}
            </Heading>
          </ModalHeader>
          <ModalBody id={`room-details-${id}-body`}>
            {description && detailsVariant === 'modal' && (
              <div className="margin-2-b">
                <Heading level={4} variant="sans-small-bold" className="margin-1-b">
                  Description
                </Heading>
                <div
                  className="body"
                  // eslint-disable-next-line react/no-danger -- room content is sourced from trusted internal API
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </div>
            )}
            {amenities.length > 0 && (
              <div>
                <Heading level={4} variant="sans-small-bold" className="margin-1-b">
                  Amenities
                </Heading>
                <ul className="body list margin-0 pad-l">
                  {amenities.map((amenity) => (
                    <li key={amenity}>{amenity}</li>
                  ))}
                </ul>
              </div>
            )}
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

const RoomShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  amenities: PropTypes.arrayOf(PropTypes.string),
  detailsVariant: PropTypes.oneOf(['inline', 'inline-modal', 'modal', 'none']),
  size: PropTypes.shape({
    value: PropTypes.number.isRequired,
    unit: PropTypes.string.isRequired,
  }),
  maxOccupancy: PropTypes.number,
  bedSummary: PropTypes.string,
  photos: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string.isRequired,
    alt: PropTypes.string,
  })),
  pricing: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    strikethroughAmount: PropTypes.number,
    strikethroughCurrency: PropTypes.string,
    boardLabel: PropTypes.string,
    refundLabel: PropTypes.string,
    freeCancellationUntil: PropTypes.string,
    paymentLabel: PropTypes.string,
  }),
});

HotelRoomCard.propTypes = {
  room: RoomShape.isRequired,
  isLast: PropTypes.bool,
};

RoomImageColumn.propTypes = {
  roomId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  roomName: PropTypes.string,
  photos: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string.isRequired,
    alt: PropTypes.string,
  })),
};

RoomPricingColumn.propTypes = {
  formattedPrice: PropTypes.string,
  formattedStrikethroughPrice: PropTypes.string,
  boardLabel: PropTypes.string,
  refundLabel: PropTypes.string,
  cancellationDate: PropTypes.string,
};

export { RoomShape };
export default HotelRoomCard;
